import { CircleAlert } from "lucide-react";
import { useState } from "react";
import { AsyncCombobox } from "@/components/shared/async-combobox";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useExtractedValues } from "@/hooks/use-extracted-values";
import type { MasterDataEntityType } from "@/lib/enums";

interface MasterDataSelectProps {
  targetBuId: string;
  entityType: MasterDataEntityType;
  value: string | undefined;
  onChange: (value: string) => void;
  /** Omit inside a table cell whose column header already carries the label (and the mapped/required markers). */
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * A field's label, decorated with a red `*` when the InsertContract XSD
 * requires it and an amber icon when its value gets rewritten to a
 * target-BU-specific master-data ID on copy. Shared by every labeled input
 * in the staging editor so both markers stay visually consistent.
 */
export function FieldLabel({
  label,
  required,
  mapped,
}: {
  label: string;
  required?: boolean;
  mapped?: boolean;
}) {
  return (
    <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
      {required && (
        <span className="text-destructive" title="Required by Coach Manager">
          *
        </span>
      )}
      {mapped && (
        <span title="Transformed to a target-BU-specific value on copy">
          <CircleAlert
            className="size-3 shrink-0 text-amber-500"
            aria-label="Transformed to a target-BU-specific value on copy"
          />
        </span>
      )}
    </p>
  );
}

/**
 * The "Target BU reference column" dropdown — every master/reference-data
 * field (Operator, Base, Vehicle Type, Facility, Included Item, Contract
 * Data Title, Contract Movement Data Title) in the staging editor renders as
 * this instead of a free-text input. Options come from the target BU's
 * `master_data_extracted_value` cache (`useExtractedValues`); typing a value
 * not yet in that cache is still accepted (`allowCustomValue`) since the
 * cache only reflects values Contract Search has previously seen there.
 * Every field routed through this component is, by construction, mapped to
 * a target-BU-specific value — when `label` is given (grid usage, no
 * surrounding column header), its label always carries the mapped icon.
 */
export function MasterDataSelect({
  targetBuId,
  entityType,
  value,
  onChange,
  label,
  required,
  disabled,
  className,
}: MasterDataSelectProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isFetching } = useExtractedValues(
    targetBuId,
    entityType,
    debouncedSearch,
    !disabled,
  );
  const values = data ?? [];

  return (
    <div className="space-y-1">
      {label !== undefined && <FieldLabel label={label} required={required} mapped />}
      <AsyncCombobox
        value={value ?? null}
        onChange={(next) => onChange(next)}
        options={values}
        getOptionValue={(v) => v}
        getOptionText={(v) => v}
        getOptionLabel={(v) => <span>{v}</span>}
        search={search}
        onSearchChange={setSearch}
        selectedLabel={value}
        loading={isFetching}
        disabled={disabled}
        placeholder="Select a value…"
        searchPlaceholder="Filter or type a new value…"
        emptyText="No values seen yet for this BU — type to use a new one."
        allowCustomValue
        getCustomOption={(text) => text}
        className={className}
      />
    </div>
  );
}

/** A numeric master-data field (BaseSerialNo, *TitleSerialNo) — wraps `MasterDataSelect` with number<->string coercion. */
export function NumericMasterDataSelect({
  targetBuId,
  entityType,
  value,
  onChange,
  label,
  required,
  disabled,
  className,
}: {
  targetBuId: string;
  entityType: MasterDataEntityType;
  value: number;
  onChange: (value: number) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <MasterDataSelect
      targetBuId={targetBuId}
      entityType={entityType}
      value={String(value)}
      onChange={(next) => {
        const parsed = Number(next);
        if (!Number.isNaN(parsed)) onChange(parsed);
      }}
      label={label}
      required={required}
      disabled={disabled}
      className={className}
    />
  );
}

/** A plain editable text field, labeled, for the staging editor's non-reference columns. */
export function TextField({
  label,
  value,
  onChange,
  required,
  mapped,
  disabled,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  mapped?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <FieldLabel label={label} required={required} mapped={mapped} />
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-8 text-sm"
      />
    </div>
  );
}

/** A plain editable number field, labeled. */
export function NumberField({
  label,
  value,
  onChange,
  required,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <FieldLabel label={label} required={required} />
      <Input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const parsed = Number(e.target.value);
          if (!Number.isNaN(parsed)) onChange(parsed);
        }}
        disabled={disabled}
        className="h-8 text-sm"
      />
    </div>
  );
}
